import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { sendOtp, verifyOtp, googleLogin } from "../api/auth";
import { useAuth } from "../context/AuthContext";

const STEPS = { EMAIL: "email", OTP: "otp" };

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [mounted, setMounted] = useState(false);

  const otpRefs = useRef([]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown((v) => v - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await sendOtp(email);
      setStep(STEPS.OTP);
      setSuccess("OTP sent! Check your inbox.");
      setResendCooldown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError("");
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
    if (newOtp.every((d) => d !== "")) handleVerify(newOtp.join(""));
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newOtp = [...otp];
    pasted.split("").forEach((char, i) => { newOtp[i] = char; });
    setOtp(newOtp);
    const nextEmpty = newOtp.findIndex((d) => !d);
    (nextEmpty === -1 ? otpRefs.current[5] : otpRefs.current[nextEmpty])?.focus();
    if (pasted.length === 6) handleVerify(pasted);
  };

  // const handleVerify = async (code) => {
  //   // if (loading) return;
  //   setError("");
  //   setLoading(true);
  //   try {
  //     const { data } = await verifyOtp(email, code);
  //     login(data.user);
  //     navigate("/welcome");
  //   } catch (err) {
  //     setError(err.response?.data?.message || "Invalid OTP");
  //     setOtp(["", "", "", "", "", ""]);
  //     setTimeout(() => otpRefs.current[0]?.focus(), 100);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
const verifyLock = useRef(false);

const handleVerify = async (code) => {
  if (!code || code.length !== 6) return;

  if (verifyLock.current) return;

  verifyLock.current = true;
  setLoading(true);
  setError("");

  try {
    const { data } = await verifyOtp(email, code);

    login(data.user);
    navigate("/welcome");
  } catch (err) {
    setError(err.response?.data?.message || "Invalid OTP");
    setOtp(["", "", "", "", "", ""]);
    setTimeout(() => otpRefs.current[0]?.focus(), 100);
  } finally {
    setLoading(false);

    // IMPORTANT: delay unlock slightly to avoid double calls
    setTimeout(() => {
      verifyLock.current = false;
    }, 300);
  }
};
  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    setLoading(true);
    try {
      const { data } = await googleLogin(credentialResponse.credential);
      login(data.user);
      navigate("/welcome");
    } catch (err) {
      setError(err.response?.data?.message || "Google login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      await sendOtp(email);
      setSuccess("New OTP sent!");
      setOtp(["", "", "", "", "", ""]);
      setResendCooldown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --ink: #0c0c0f;
          --ink-2: #1a1a24;
          --ink-3: #252532;
          --muted: #6b6b80;
          --muted-2: #9494a8;
          --line: rgba(255,255,255,0.07);
          --accent: #7c6af7;
          --accent-glow: rgba(124,106,247,0.25);
          --accent-2: #e879a0;
          --surface: rgba(255,255,255,0.03);
          --white: #f4f4f8;
          --success: #34d399;
          --error: #f87171;
        }

        html, body { height: 100%; background: var(--ink); color: var(--white); font-family: 'DM Sans', sans-serif; }

        .login-root {
          min-height: 100vh;
          display: grid;
          place-items: center;
          background:
            radial-gradient(ellipse 80% 60% at 50% -10%, rgba(124,106,247,0.18) 0%, transparent 60%),
            radial-gradient(ellipse 40% 40% at 80% 80%, rgba(232,121,160,0.1) 0%, transparent 50%),
            var(--ink);
          padding: 24px;
          position: relative;
          overflow: hidden;
        }

        .login-root::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.015) 1px, transparent 1px);
          background-size: 32px 32px;
          pointer-events: none;
        }

        .card {
          width: 100%;
          max-width: 420px;
          background: var(--ink-2);
          border: 1px solid var(--line);
          border-radius: 24px;
          padding: 48px 40px;
          position: relative;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.5s ease, transform 0.5s ease;
          backdrop-filter: blur(12px);
          box-shadow: 0 0 0 1px rgba(255,255,255,0.04), 0 32px 64px rgba(0,0,0,0.5);
        }

        .card.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 40px;
        }

        .logo-mark {
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, var(--accent), var(--accent-2));
          border-radius: 10px;
          display: grid;
          place-items: center;
          font-size: 16px;
          flex-shrink: 0;
        }

        .logo-text {
          font-family: 'Instrument Serif', serif;
          font-size: 18px;
          letter-spacing: -0.3px;
          color: var(--white);
        }

        .heading {
          font-family: 'Instrument Serif', serif;
          font-size: 28px;
          font-weight: 400;
          line-height: 1.2;
          color: var(--white);
          margin-bottom: 8px;
          letter-spacing: -0.5px;
        }

        .subheading {
          font-size: 14px;
          color: var(--muted);
          margin-bottom: 36px;
          line-height: 1.5;
        }

        .subheading em {
          font-style: normal;
          color: var(--muted-2);
        }

        .input-group {
          margin-bottom: 16px;
        }

        label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: var(--muted-2);
          letter-spacing: 0.8px;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        input[type="email"] {
          width: 100%;
          background: var(--surface);
          border: 1px solid var(--line);
          border-radius: 12px;
          padding: 14px 16px;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          color: var(--white);
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        input[type="email"]:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-glow);
        }

        input[type="email"]::placeholder { color: var(--muted); }

        .btn {
          width: 100%;
          padding: 14px;
          border-radius: 12px;
          border: none;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
          overflow: hidden;
        }

        .btn-primary {
          background: linear-gradient(135deg, var(--accent), #9b8ff8);
          color: white;
          box-shadow: 0 4px 16px rgba(124,106,247,0.3);
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(124,106,247,0.4);
        }

        .btn-primary:active:not(:disabled) { transform: translateY(0); }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

        .divider {
          display: flex;
          align-items: center;
          gap: 12px;
          margin: 24px 0;
          color: var(--muted);
          font-size: 12px;
          letter-spacing: 1px;
        }

        .divider::before, .divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--line);
        }

        .google-wrap {
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid var(--line);
          transition: border-color 0.2s;
        }
        .google-wrap:hover { border-color: rgba(255,255,255,0.15); }

        /* OTP inputs */
        .otp-row {
          display: flex;
          gap: 10px;
          justify-content: center;
          margin-bottom: 28px;
        }

        .otp-input {
          width: 52px;
          height: 60px;
          background: var(--ink-3);
          border: 1.5px solid var(--line);
          border-radius: 12px;
          text-align: center;
          font-size: 24px;
          font-weight: 600;
          color: var(--white);
          outline: none;
          font-family: 'DM Sans', monospace;
          transition: border-color 0.2s, box-shadow 0.2s, transform 0.1s;
          caret-color: var(--accent);
        }

        .otp-input:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-glow);
          transform: translateY(-2px);
        }

        .otp-input.filled {
          border-color: rgba(124,106,247,0.5);
          background: rgba(124,106,247,0.08);
        }

        .resend-row {
          text-align: center;
          margin-top: 16px;
        }

        .resend-btn {
          background: none;
          border: none;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          cursor: pointer;
          transition: color 0.2s;
          padding: 4px 8px;
        }

        .resend-btn.active { color: var(--accent); }
        .resend-btn.active:hover { color: #9b8ff8; }
        .resend-btn.cooldown { color: var(--muted); cursor: default; }

        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: none;
          border: none;
          color: var(--muted);
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          cursor: pointer;
          padding: 0;
          margin-bottom: 32px;
          transition: color 0.2s;
        }
        .back-btn:hover { color: var(--white); }

        .alert {
          padding: 12px 14px;
          border-radius: 10px;
          font-size: 13px;
          margin-bottom: 20px;
          line-height: 1.4;
        }

        .alert-error {
          background: rgba(248,113,113,0.1);
          border: 1px solid rgba(248,113,113,0.25);
          color: var(--error);
        }

        .alert-success {
          background: rgba(52,211,153,0.1);
          border: 1px solid rgba(52,211,153,0.25);
          color: var(--success);
        }

        .spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
          vertical-align: middle;
          margin-right: 8px;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        .email-chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(124,106,247,0.12);
          border: 1px solid rgba(124,106,247,0.25);
          border-radius: 999px;
          padding: 4px 12px;
          font-size: 13px;
          color: #a89ff9;
          margin: 0 auto 8px;
        }
      `}</style>

      <div className="login-root">
        <div className={`card ${mounted ? "visible" : ""}`}>
          <div className="logo">
            <div className="logo-mark"><img src="./logo.jpg" width={40} height={50} alt='logo'/></div>
            <span className="logo-text">VRSAFETECH</span>
          </div>

          {step === STEPS.EMAIL ? (
            <>
              <h1 className="heading">Welcome back.</h1>
              <p className="subheading">Sign in to continue — <em>no password needed.</em></p>

              {error && <div className="alert alert-error">⚠ {error}</div>}

              <form onSubmit={handleSendOtp}>
                <div className="input-group">
                  <label htmlFor="email">Email address</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                <button type="submit" className="btn btn-primary" disabled={loading || !email}>
                  {loading ? <><span className="spinner" />Sending OTP...</> : "Send OTP →"}
                </button>
              </form>

              <div className="divider">or continue with</div>

              <div className="google-wrap">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError("Google sign-in failed")}
                  theme="filled_black"
                  shape="rectangular"
                  width={350}
                  text="continue_with"
                />
              </div>
            </>
          ) : (
            <>
              <button className="back-btn" onClick={() => { setStep(STEPS.EMAIL); setError(""); setSuccess(""); setOtp(["","","","","",""]); }}>
                ← Back
              </button>

              <h1 className="heading">Check your inbox.</h1>
              <p className="subheading" style={{ marginBottom: 20 }}>We sent a 6-digit code to</p>
              <div style={{ textAlign: "center", marginBottom: 32 }}>
                <span className="email-chip">✉ {email}</span>
              </div>

              {error && <div className="alert alert-error">⚠ {error}</div>}
              {success && <div className="alert alert-success">✓ {success}</div>}

              <div className="otp-row" onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className={`otp-input ${digit ? "filled" : ""}`}
                    disabled={loading}
                  />
                ))}
              </div>

              <button
                className="btn btn-primary"
                onClick={() => handleVerify(otp.join(""))}
                disabled={loading || otp.some((d) => !d)}
              >
                {loading ? <><span className="spinner" />Verifying...</> : "Verify & Sign in →"}
              </button>

              <div className="resend-row">
                <button
                  className={`resend-btn ${resendCooldown > 0 ? "cooldown" : "active"}`}
                  onClick={handleResend}
                  disabled={loading || resendCooldown > 0}
                >
                  {resendCooldown > 0
                    ? `Resend in ${resendCooldown}s`
                    : "Didn't get it? Resend OTP"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
